import { useRouter } from 'next/router';
import { useCallback } from 'react';

import type { TradeDirection } from '@/components/TransactionModule/types';
import { isSameQuery } from '@/components/TransactionModule/utils';

import { useTokenResult } from '@/hooks/tokenQueries/useTokenResult';
import { CheckoutModes, useCheckoutMode } from '@/hooks/useCheckoutMode';
import { useInitializeDefaultTokens } from '@/hooks/useInitializeDefaultTokens';
import { useSelectedTokens } from '@/hooks/useSelectedTokens';

import { TokenBase } from '@/utils/0x/token-registry.types';
import { ChainAddress } from '@/utils/models';
import { routes } from '@/utils/routes';
import type { DefaultTokenPair } from '@/utils/swap';
import type { TokenInfo } from '@/utils/token.types';
import { pickChartToken, resolveTradeRoute, resolveTradeTokens } from '@/utils/tradeRoutes';
import type { TokenRef } from '@/utils/url';

import { resolveSharedChainToken } from './resolveSharedChainToken';
import { navigateTradeRoute } from './tradeRouteNavigation';

type TokenPairPathname = '/' | '/trade';

type UrlMethod = 'push' | 'replace';

// The homepage derives defaults from its route, so initialization never writes the URL.
const skipUrlSync = () => {};

export const useUrlTokenPair = (pathname: TokenPairPathname) => {
  const router = useRouter();
  const { push, replace, query } = router;
  const [checkoutMode] = useCheckoutMode();

  const isHomepage = pathname === routes.INDEX;
  const selectedTokens = useSelectedTokens();
  // Read prefetched defaults on the first render instead of waiting for the URL effect.
  const route =
    isHomepage && router.isReady ? resolveTradeRoute(router.pathname, query) : undefined;
  const tradeType = route?.tradeType;
  const { chartToken, buyTokenAddress, buyTokenChainId, sellTokenAddress, sellTokenChainId } =
    route ??
    (router.isReady ? resolveTradeTokens(query) : { ...selectedTokens, chartToken: undefined });

  const chartAddress = chartToken?.address;
  const chartChainId = chartToken?.chainId;

  const { token: sellToken } = useTokenResult(sellTokenAddress, sellTokenChainId);
  const { token: buyToken } = useTokenResult(buyTokenAddress, buyTokenChainId);

  const navigate = useCallback(
    (method: UrlMethod, next: DefaultTokenPair, nextChartToken?: TokenRef) => {
      if (isHomepage && tradeType) {
        navigateTradeRoute(router, method, {
          ...next,
          tradeType,
          chartToken: nextChartToken,
        });
        return;
      }

      const { buyAddress, buyChain, sellAddress, sellChain, ...otherParams } = query;
      const newQuery = { ...otherParams };

      if (next.buyTokenAddress) newQuery.buyAddress = next.buyTokenAddress;
      if (next.buyTokenChainId) newQuery.buyChain = next.buyTokenChainId.toString();
      if (next.sellTokenAddress) newQuery.sellAddress = next.sellTokenAddress;
      if (next.sellTokenChainId) newQuery.sellChain = next.sellTokenChainId.toString();

      if (isSameQuery(newQuery, query)) return;
      const navigateUrl = method === 'push' ? push : replace;
      navigateUrl({ pathname, query: newQuery }, undefined, { shallow: true });
    },
    [isHomepage, pathname, push, query, replace, router, tradeType],
  );

  const syncUrlParameters = useCallback(
    (next: DefaultTokenPair) => {
      const current =
        chartAddress && chartChainId ? { address: chartAddress, chainId: chartChainId } : undefined;
      navigate('push', next, pickChartToken(next, current));
    },
    [chartAddress, chartChainId, navigate],
  );

  // Replace defaults so Back can leave the page, including inside an iframe.
  const replaceUrlParameters = useCallback(
    (
      newBuyAddress?: ChainAddress,
      newBuyChain?: number,
      newSellAddress?: ChainAddress,
      newSellChain?: number,
    ) =>
      navigate('replace', {
        buyTokenAddress: newBuyAddress,
        buyTokenChainId: newBuyChain,
        sellTokenAddress: newSellAddress,
        sellTokenChainId: newSellChain,
      }),
    [navigate],
  );

  useInitializeDefaultTokens({
    ...selectedTokens,
    syncUrlParameters: isHomepage ? skipUrlSync : replaceUrlParameters,
  });

  const handleFlipSelectedTokens = useCallback(() => {
    syncUrlParameters({
      buyTokenAddress: sellTokenAddress,
      buyTokenChainId: sellTokenChainId,
      sellTokenAddress: buyTokenAddress,
      sellTokenChainId: buyTokenChainId,
    });
  }, [buyTokenAddress, buyTokenChainId, sellTokenAddress, sellTokenChainId, syncUrlParameters]);

  const handleSelectToken = useCallback(
    (selectedToken: Pick<TokenBase, 'address' | 'chainId'>, side: TradeDirection) => {
      const { address, chainId } = selectedToken;

      if (side === 'sell') {
        setTimeout(() => document.getElementById('sellAmount')?.focus(), 0);
      }

      const advanced = checkoutMode === CheckoutModes.advanced;
      const oppositeTokenAddress =
        side === 'sell' && !advanced && chainId === sellTokenChainId
          ? undefined
          : resolveSharedChainToken(
              selectedToken,
              side,
              side === 'buy' ? sellTokenChainId : buyTokenChainId,
              { advanced },
            );

      syncUrlParameters(
        side === 'buy'
          ? {
              buyTokenAddress: address,
              buyTokenChainId: chainId,
              sellTokenAddress: oppositeTokenAddress ?? sellTokenAddress,
              sellTokenChainId: oppositeTokenAddress ? chainId : sellTokenChainId,
            }
          : {
              buyTokenAddress: oppositeTokenAddress ?? buyTokenAddress,
              buyTokenChainId: oppositeTokenAddress ? chainId : buyTokenChainId,
              sellTokenAddress: address,
              sellTokenChainId: chainId,
            },
      );
    },
    [
      buyTokenAddress,
      buyTokenChainId,
      checkoutMode,
      syncUrlParameters,
      sellTokenAddress,
      sellTokenChainId,
    ],
  );

  const handleSelectPair = useCallback(
    (nextBuyToken: TokenInfo, nextSellToken: TokenInfo) => {
      syncUrlParameters({
        buyTokenAddress: nextBuyToken.address,
        buyTokenChainId: nextBuyToken.chainId,
        sellTokenAddress: nextSellToken.address,
        sellTokenChainId: nextSellToken.chainId,
      });
    },
    [syncUrlParameters],
  );

  const handleSelectChartToken = useCallback(
    (token: TokenRef) =>
      navigate(
        'push',
        { buyTokenAddress, buyTokenChainId, sellTokenAddress, sellTokenChainId },
        token,
      ),
    [navigate, buyTokenAddress, buyTokenChainId, sellTokenAddress, sellTokenChainId],
  );

  return {
    buyToken,
    sellToken,
    buyTokenAddress,
    buyTokenChainId,
    sellTokenAddress,
    sellTokenChainId,
    chartToken,
    handleFlipSelectedTokens,
    handleSelectToken,
    handleSelectPair,
    handleSelectChartToken,
  };
};
